/**
 * ClockStatusCard
 * ---------------
 * Inline clock-in / clock-out shortcut for the staff dashboard.
 *
 * Reuses the existing time-clock API — no new endpoints, no duplicate logic:
 *   timeClockAPI.getActiveShift()
 *   timeClockAPI.clockIn()
 *   timeClockAPI.clockOut()
 *
 * Renders:
 *   - "Not Clocked In"  state with orange  Clock In button
 *   - "Clocked In"      state with running duration, GPS-verified hint
 *                       (only shown if backend returns gps_verified), and
 *                       a Clock Out button.
 *
 * Refreshes via gentle polling (60s) so duration stays current without
 * websockets.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Clock, MapPin, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { timeClockAPI } from '../lib/api';

function formatDuration(startIso) {
  if (!startIso) return '—';
  const start = new Date(startIso).getTime();
  if (Number.isNaN(start)) return '—';
  const diffMs = Math.max(0, Date.now() - start);
  const totalMin = Math.floor(diffMs / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

export default function ClockStatusCard() {
  const [activeShift, setActiveShift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  // tick state forces duration re-render every minute
  const [, setTick] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const { data } = await timeClockAPI.getActiveShift();
      setActiveShift(data || null);
    } catch (err) {
      // Non-fatal: if endpoint fails (e.g. no permissions, no restaurant), render
      // the "Not clocked in" state silently.
      setActiveShift(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const poll = setInterval(refresh, 60000); // 60s polling
    const tickInt = setInterval(() => setTick(t => t + 1), 30000); // 30s for live duration
    return () => {
      clearInterval(poll);
      clearInterval(tickInt);
    };
  }, [refresh]);

  const handleClockIn = async () => {
    if (acting) return;
    setActing(true);
    try {
      await timeClockAPI.clockIn();
      toast.success('Clocked in');
      await refresh();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not clock in';
      toast.error(msg);
    } finally {
      setActing(false);
    }
  };

  const handleClockOut = async () => {
    if (acting) return;
    setActing(true);
    try {
      await timeClockAPI.clockOut();
      toast.success('Clocked out');
      await refresh();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not clock out';
      toast.error(msg);
    } finally {
      setActing(false);
    }
  };

  const clockedIn = !!activeShift;
  const duration = clockedIn ? formatDuration(activeShift.start_time) : null;
  const gpsVerified = clockedIn && activeShift.gps_verified === true;

  return (
    <div
      className="bg-white rounded-xl border border-slate-200 shadow-sm p-5"
      data-testid="clock-status-card"
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ${
              clockedIn ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-500'
            }`}
          >
            <Clock size={22} />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Clock Status
            </p>
            {loading ? (
              <p className="text-base font-semibold text-slate-400 mt-0.5">Loading…</p>
            ) : clockedIn ? (
              <p
                className="text-base font-semibold text-slate-900 mt-0.5"
                data-testid="clock-status-state"
              >
                Clocked in
                <span className="text-slate-500 font-normal"> · {duration}</span>
              </p>
            ) : (
              <p
                className="text-base font-semibold text-slate-900 mt-0.5"
                data-testid="clock-status-state"
              >
                Not clocked in
              </p>
            )}
            {clockedIn && gpsVerified && (
              <p
                className="text-xs text-green-600 mt-0.5 inline-flex items-center gap-1"
                data-testid="clock-status-gps"
              >
                <MapPin size={12} /> Location verified
              </p>
            )}
          </div>
        </div>

        {clockedIn ? (
          <Button
            type="button"
            onClick={handleClockOut}
            disabled={acting}
            className="bg-slate-900 hover:bg-slate-800 text-white min-h-[44px] px-5"
            data-testid="clock-status-clockout-btn"
            aria-label="Clock out"
          >
            {acting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Clock Out
          </Button>
        ) : (
          <Button
            type="button"
            onClick={handleClockIn}
            disabled={acting || loading}
            className="bg-orange-500 hover:bg-orange-600 text-white min-h-[44px] px-5"
            data-testid="clock-status-clockin-btn"
            aria-label="Clock in"
          >
            {acting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Clock In
          </Button>
        )}
      </div>
    </div>
  );
}
